<template>
  <div class="sku-popup-component">
    <ym-popup class="sku-popup"
              v-model="visible"
              round
              position="bottom"
              get-container="body"
              @close="handleClosePopup"
    >
      <div v-if="product"
           class="sku-popup-product"
      >
        <ym-img class="sku-popup-product-cover"
                :src="product.cover"
        />
        <div class="sku-popup-product-content-info">
          <div class="sku-popup-product-price">
            <span class="sku-popup-product-price-unit">¥</span>{{ product.price }}
          </div>
          <div class="sku-popup-product-stock">
            库存：<span :class="{ 'is-highlight': highlightStock }">{{ product.stock }}</span>
            {{ product.unit }}
          </div>
          <div v-if="productHint !== ''"
               class="sku-popup-product-hint ym-ellipsis"
               :class="[
                 { 'is-selected-all': selectedAll }
               ]"
          >
            {{ productHint }}
          </div>
        </div>
      </div>

      <div v-if="packages.length > 0"
           class="sku-popup-content"
      >
        <div class="sku-popup-packages">
          <div class="sku-popup-pack"
               v-for="pack in packages"
               :key="pack.id"
          >
            <div class="sku-popup-pack-name">{{ pack.name }}</div>
            <div class="sku-popup-pack-items">
              <ym-button class="sku-popup-pack-item"
                         v-for="item in getPackList(pack.list)"
                         :key="item.id"
                         :type="item.selected ? 'primary-light' : 'default'"
                         size="small"
                         :disabled="item.stock === 0"
                         @click="handleSelected(pack, item)"
              >
                {{ item.name }}
              </ym-button>
            </div>
          </div>
        </div>
      </div>

      <div class="sku-popup-count">
        <div class="sku-popup-pack-name">{{ config.stepperConfig.title }}</div>

        <div class="sku-popup-count--right">
          <div v-if="stepperConfig.hideQuotaText !== true"
               class="sku-popup-count-quota-text"
          >
            {{ quotaHint }}
          </div>
          <ym-stepper class="sku-popup-count-stepper"
                      v-model="count"
                      :min="stepperConfig.min"
                      :max="stepperConfig.max"
                      :disabled-input="stepperConfig.disableInput"
                      integer
                      @over-limit="handleOverLimit"
          />
        </div>
      </div>

      <div class="sku-popup-footer"
           :class="[
             { 'is-empty-packages': config.packages.length === 0 }
           ]"
      >
        <ym-button class="sku-popup-confirm"
                   type="primary"
                   size="large"
                   @click="handleConfirm"
        >
          {{ confirmText }}
        </ym-button>
      </div>
    </ym-popup>
  </div>
</template>

<script>
  import {
    Popup, Stepper, Button,
  } from '@ymba/ym-ui';
  import event from '@/utils/bus';
  import validator from '@/utils/validator';
  import joinProductMixin from '@/mixins/order/joinProduct';
  import { MAX_QUOTA } from '@/utils/constant';
  import { formatPriceRange } from '@/utils';

  const PACKAGE_KEY_NAME = 'p';

  const DEFAULT_STEPPER_CONFIG = {
    min: 1,
    max: MAX_QUOTA,
    disableInput: false,
    hideQuotaText: true,
  };

  const DEFAULT_STOCK = 1;

  export default {
    name: 'SkuPopup',
    mixins: [joinProductMixin],
    components: {
      'ym-popup': Popup,
      'ym-stepper': Stepper,
      'ym-button': Button,
    },
    props: {
      show: {
        type: Boolean,
        required: true,
      },
      recommendProduct: {
        required: true,
        validator(val) {
          return validator.isObject(val);
        },
      },
      config: {
        type: Object,
        required: true,
        default() {
          return {
            packages: [],
          };
        },
      },
      products: {
        type: Array,
        required: true,
        default() {
          return [];
        },
      },
      initialSku: {
        validator(val) {
          return validator.isObject(val);
        },
      },
    },
    data() {
      return {
        visible: false,
        selectedProduct: null,
        packages: [],
        count: 1,
        stepperConfig: {
          ...DEFAULT_STEPPER_CONFIG,
        },
        quotaHint: '',

        selectedPackagesMap: [],
      };
    },
    computed: {
      product() {
        const { selectedProduct, recommendProduct } = this;
        const { price } = recommendProduct || {};
        if (Array.isArray(price)) {
          recommendProduct.price = formatPriceRange(price);
        }
        return selectedProduct || recommendProduct;
      },
      highlightStock() {
        const { product: { stock = '0' }, config: { stockThreshold } } = this;
        return stockThreshold > 0 && Number(stock) < stockThreshold;
      },
      unSelectHint() {
        const { packages } = this;
        const unSelectPack = packages.filter(pack => pack.selectedItem === null);
        const packNames = unSelectPack.map(pack => pack.name);
        return packNames.join('、');
      },
      selectedName() {
        const { packages } = this;
        const packNames = packages.map(pack => pack.selectedItem.name);
        return packNames.join('、');
      },
      selectedAll() {
        const selectedPack = this.getSelectedPackages();
        return selectedPack.length === this.packages.length;
      },
      productHint() {
        const { packages, selectedAll } = this;
        if (packages.length === 0) return '';
        if (selectedAll) {
          return `已选 ${this.selectedName}`;
        }
        return `请选择${this.unSelectHint}`;
      },
      showSoldOutSku() {
        const { config: { showSoldOutSku = true } } = this;
        return showSoldOutSku;
      },
      confirmText() {
        const { config: { confirmText = '确认' } } = this;
        return confirmText;
      },
    },
    watch: {
      show(val) {
        this.visible = val;
      },
      visible(val) {
        this.$emit('update:show', val);
      },
      selectedProduct(val) {
        // 配置步进器
        this.setupStepper(val);
      },
      config(val) {
        const { packages } = val;
        this.initPackages(packages, this.products);
      },
      initialSku(val) {
        if (val == null) return;
        this.handleInitialSelect(val);
      },
    },
    methods: {
      handleInitialSelect(initialSku) {
        const { count, sku } = initialSku;
        sku.forEach((selected) => {
          const { key, value } = selected;
          const packId = Number(key.replace(PACKAGE_KEY_NAME, ''));
          const curPack = this.packages.find(pack => pack.id === packId);
          const curItem = curPack.list.find(item => item.id === Number(value));
          this.handleSelected(curPack, curItem);
        });
        this.count = count;
      },
      computePackagesStock() {
        const { products } = this;
        const emptyProducts = products.filter(product => Number(product.stock) === 0);
        // 没有库存为0 的产品，退出函数
        if (emptyProducts.length === 0) return;

        const { packages, selectedPackagesMap } = this;

        packages.forEach((pack) => {
          const { id: packId } = pack;
          pack.list.forEach((packItem) => {
            const key = `${PACKAGE_KEY_NAME}${packId}`;
            const canCountProducts = products.filter((product) => {
              let res = true;

              // 必须规格必须符合当前规则
              if (product.packages[key] !== `${packItem.id}`) {
                res = false;
              }

              // 如有已选规格组，则筛选符合已选规则组的产品
              if (selectedPackagesMap.length > 0) {
                const { packages: productPack } = product;

                selectedPackagesMap.forEach((selectedPack) => {
                  // 去除与当前item 同一个组的已选筛选
                  if (selectedPack.key === key) return;
                  if (productPack[selectedPack.key] !== selectedPack.value) {
                    res = false;
                  }
                });
              }

              return res;
            });

            // 有可计算库存产品，则进行赋值
            if (canCountProducts.length > 0) {
              packItem.stock = this.countStock(canCountProducts);
            }
          });
        });
      },
      initPackages(packages, products) {
        // 默认赋值
        this.packages = packages.map((pack) => {
          pack.list = pack.list.map((item) => {
            // 默认库存为1
            item.stock = DEFAULT_STOCK;
            return item;
          });
          return pack;
        });
        // 计算真实库存
        this.computePackagesStock(products);
      },
      getPackList(list) {
        const { showSoldOutSku } = this;
        if (showSoldOutSku) return list;
        return list.filter(item => item.stock > 0);
      },
      setStepperConfig(min, quota, quotaUsed, hideHint) {
        let max = MAX_QUOTA;
        let disableInput = false;
        let hideQuotaText = true;
        if (quota > 0) {
          max = quota - quotaUsed;
          disableInput = true;
          hideQuotaText = hideHint == null ? false : hideHint;
        }
        this.stepperConfig = {
          min,
          max,
          disableInput,
          hideQuotaText,
        };
      },
      setQuotaHint(startSaleNum, quota, quotaUsed, unit) {
        const hint = [];
        if (startSaleNum > 1) {
          hint.push(`${startSaleNum}${unit}起售`);
        }
        if (quota > 0) {
          hint.push(`限购${quota}${unit}`);
        }
        if (quotaUsed > 0) {
          hint.push(`已购${quotaUsed}${unit}`);
        }
        this.quotaHint = `(${hint.join('，')})`;
      },
      setupStepper(product) {
        const { config, unit, stock } = product || {};
        if (config == null) {
          this.setStepperConfig(1, stock, 0, true);
          return;
        }
        const {
          startSaleNum = 1, quota = 0, quotaUsed = 0, hideQuotaText,
        } = config;
        // 设置配置
        this.setStepperConfig(startSaleNum, quota, quotaUsed, hideQuotaText);
        // 设置提示
        this.setQuotaHint(startSaleNum, quota, quotaUsed, unit);
      },
      filterProductsWithPackage(products, key, value) {
        return products.filter(product => `${product.packages[key]}` === `${value}`);
      },
      countStock(products) {
        return products.map(product => Number(product.stock)).reduce((acc, cur) => acc + cur);
      },
      getSelectedPackages() {
        return this.packages.filter(pack => pack.selectedItem !== null);
      },
      computeSelectedPackagesMap() {
        const selectedPackages = this.getSelectedPackages();
        this.selectedPackagesMap = selectedPackages.map(pack => ({
          key: `${PACKAGE_KEY_NAME}${pack.id}`,
          value: `${pack.selectedItem.id}`,
          name: pack.selectedItem.name,
        }));
      },
      setPackSelectedItem(pack, item) {
        // 赋值
        pack.selectedItem = item;
        // 更新对应Map
        this.computeSelectedPackagesMap();
      },
      toggleItemSelect(pack, item, selected) {
        // 设置整组选中与否
        this.setPackSelectedItem(pack, selected ? item : null);
        // 设置当前子项选中与否
        item.selected = selected;
      },
      getConditionProducts(products) {
        let res = products;

        // 处理有已选的规格组
        const { selectedPackagesMap } = this;
        if (selectedPackagesMap.length > 0) {
          selectedPackagesMap.forEach((select) => {
            res = this.filterProductsWithPackage(res, select.key, select.value);
          });
        }

        return res;
      },
      handleUniqueProduct(products) {
        const [selectedProduct] = products;
        this.selectedProduct = selectedProduct;
      },
      getProductPriceRange(products) {
        const prices = products.map(product => product.price);
        const minPrice = Math.min.apply(this, prices);
        const maxPrice = Math.max.apply(this, prices);
        return formatPriceRange([minPrice, maxPrice]);
      },
      handleMultipleProduct(products) {
        const {
          cover, unit, targetId, targetType,
        } = products[0];
        const price = this.getProductPriceRange(products);
        const stock = this.countStock(products);
        this.selectedProduct = {
          cover,
          price,
          stock,
          unit,
          targetId,
          targetType,
        };
      },
      handleSelectedProduct(products) {
        const conditionProducts = this.getConditionProducts(products);
        // 没有符合条件的产品
        if (conditionProducts.length === 0) return;
        // 选中唯一产品
        if (conditionProducts.length === 1) {
          this.handleUniqueProduct(conditionProducts);
          return;
        }
        // 多个产品符合条件
        this.handleMultipleProduct(conditionProducts);
      },
      handleCancelSelected(pack, item) {
        // 取消选中
        this.toggleItemSelect(pack, item, false);

        // 重置选中产品
        this.handleSelectedProduct(this.products);

        // 重置packages
        this.computePackagesStock(this.products);
      },
      computeSkuView(products) {
        // 处理选择商品
        this.handleSelectedProduct(products);

        // 处理库存
        this.computePackagesStock(products);
      },
      handleSelected(pack, item) {
        const { selected, stock } = item;
        // 无库存Sku 无法操作
        if (Number(stock) <= 0) return;

        // 取消选中
        if (selected) {
          this.handleCancelSelected(pack, item);
          return;
        }

        const { id: packId } = pack;
        const { products: productList } = this;
        const key = `${PACKAGE_KEY_NAME}${packId}`;
        const products = this.filterProductsWithPackage(productList, key, item.id);
        if (products.length > 0) {
          // 处理当前规格组中的切换
          if (pack.selectedItem !== null) {
            const { selectedItem: { id: selectedId } } = pack;
            const selectedItem = pack.list.find(packItem => packItem.id === selectedId);
            // 取消之前的选择
            this.handleCancelSelected(pack, selectedItem);
          }

          // 设置选中
          this.toggleItemSelect(pack, item, true);

          // 计算规格展示
          this.computeSkuView(products);
        }
      },
      validatorSelected() {
        const res = {
          valid: true,
          msg: '',
        };
        const { packages } = this;
        packages.forEach((pack) => {
          if (pack.selectedItem == null) {
            res.valid = false;
            res.msg = `请选择 ${pack.name}`;
          }
        });
        return res;
      },
      handleConfirm() {
        const res = this.validatorSelected();
        if (!res.valid) {
          const { msg } = res;
          if (msg !== '') {
            this.$toast(msg);
          }
          return;
        }
        const { selectedProduct: { targetType, targetId } } = this;
        // 调用mixin 购买产品
        this._handleCreateOrder(targetId, targetType, this.count);
      },
      handleClosePopup() {
        const {
          selectedAll, selectedPackagesMap, count, product: { unit },
        } = this;
        const value = selectedAll ? { sku: selectedPackagesMap, count, unit } : null;
        event.$emit('sku-selected-all', value);
      },
      handleOverLimit(type) {
        let message = '不能再减少了';
        if (type === 'plus') {
          message = '不能再增多了';
        }
        this.$toast(message);
      },
    },
    created() {
      this.visible = this.show;
      this.initPackages(this.config.packages, this.products);
    },
  };
</script>

<style scoped lang="less">
  @import '~@/assets/styles/var.less';

  .sku-popup {
    padding: @size-base;

    &-product {
      display: flex;
      align-items: flex-end;
      margin-bottom: @size-extra-medium;
      overflow: hidden;
      color: @color-text-lighter;

      @sku-product-cover: 150px;

      &-cover {
        display: block;
        flex-shrink: 0;
        overflow: hidden;
        width: @sku-product-cover;
        height: @sku-product-cover;
        margin-right: @size-small;
        border-radius: @border-radius-normal;
      }

      &-content-info {
        align-self: stretch;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        overflow: hidden;
        padding: @size-extra-small 0;
        font-size: @font-size-small;
        line-height: 1;
      }

      &-price {
        margin-bottom: @size-small;
        font-size: @font-size-large;
        line-height: 1.4;
        color: @color-highlight;

        &-unit {
          font-size: @font-size-extra-small;
        }
      }

      &-stock {
        color: @color-text-lighter;

        .is-highlight {
          color: @color-text-highlight;
        }
      }

      &-hint {
        margin-top: @size-extra-small;

        &.is-selected-all {
          color: @color-text-base;
        }
      }
    }

    &-content {
      max-height: 828px;
      overflow-x: hidden;
      overflow-y: scroll;
      margin-bottom: @size-medium;
      border-bottom: @border-base;
    }

    &-pack {
      margin-bottom: @size-medium;

      &-name {
        font-size: @font-size-base;
        line-height: 1.2;
        color: @color-text-base;
      }

      &-items {
        margin-top: @size-base - @size-small;
      }

      &-item {
        border-radius: @border-radius-normal;

        &.is-disabled {
          opacity: 1;
          color: @color-disabled;
          border-color: @background-color-lighter;
          background-color: @background-color-lighter;
        }

        &:not(:last-child) {
          margin: @size-small @size-small 0 0;
        }
        &:last-child {
          margin-top: @size-small;
        }
      }
    }

    &-count {
      display: flex;
      align-items: center;
      justify-content: space-between;

      /deep/ .ym-stepper-input {
        color: @color-text-base;
      }

      &--right {
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }

      &-quota-text {
        font-size: @font-size-small;
        color: @color-highlight;
        margin-right: @size-small;
      }

      &-stepper {
        /deep/ .ym-stepper-input {
          -webkit-text-fill-color: @color-text-base;
          color: @color-text-base;
          opacity: 1;
        }
      }
    }

    &-footer {
      padding-top: @size-extra-large;

      &.is-empty-packages {
        padding-top: 320px;
      }
    }

    &-confirm {
      border-radius: @border-radius-normal;
    }
  }
</style>
